import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, MailOpen, X } from "lucide-react";
import BrandWordmark from "@/components/site/BrandWordmark";

const SECTORS = ["Oil & Gas", "Power & Utilities", "Healthcare", "Automotive", "IT & Data Centers", "Skill Training", "Real Estate"];

const EMPTY = { title: "", client: "", location: "", scope: "", year: `${new Date().getFullYear()}`, sector: SECTORS[0], image_url: "" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // project object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!localStorage.getItem("srih_admin_token")) {
      nav("/admin/login");
      return;
    }
    refresh();
  }, []);

  async function refresh() {
    try {
      const [p, c] = await Promise.all([api.get("/projects"), api.get("/contacts")]);
      setProjects(p.data);
      setContacts(c.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("srih_admin_token");
        nav("/admin/login");
      }
    }
  }

  function logout() {
    localStorage.removeItem("srih_admin_token");
    nav("/admin/login");
  }

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  }
  function openEdit(p) {
    setEditing(p);
    setForm({
      title: p.title, client: p.client, location: p.location, scope: p.scope,
      year: p.year, sector: p.sector, image_url: p.image_url || "",
    });
    setShowForm(true);
  }

  async function saveProject(e) {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project added");
      }
      setShowForm(false);
      refresh();
    } catch {
      toast.error("Save failed");
    }
  }

  async function delProject(id) {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    refresh();
  }

  async function markRead(id) {
    await api.patch(`/contacts/${id}/read`);
    refresh();
  }
  async function delContact(id) {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Deleted");
    refresh();
  }

  const unread = contacts.filter((c) => !c.read).length;

  return (
    <div className="min-h-screen relative">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#0A1F44]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <BrandWordmark variant="compact" onDark={true} />
            <div className="hidden md:block font-mono text-[10px] text-white/50 uppercase tracking-widest border-l border-white/15 pl-6">Admin · Control room</div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="btn-ghost px-3 py-2 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
          >
            <LogOut size={12} /> Log out
          </button>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center gap-6 border-t border-white/10">
          {[
            { k: "projects", label: `Projects (${projects.length})` },
            { k: "contacts", label: `Contacts (${unread} new)` },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`py-3 font-mono text-[11px] uppercase tracking-widest border-b-2 ${
                tab === t.k ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {tab === "projects" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display uppercase text-3xl">Projects</h1>
              <button
                onClick={openNew}
                data-testid="admin-add-project"
                className="btn-volt px-4 py-2 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
              >
                <Plus size={12} /> Add project
              </button>
            </div>

            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F2952] font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Client</th>
                    <th className="text-left px-4 py-3">Sector</th>
                    <th className="text-left px-4 py-3">Location</th>
                    <th className="text-left px-4 py-3">Year</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} data-testid={`admin-project-row-${p.id}`} className="border-t border-white/10">
                      <td className="px-4 py-3">{p.title}</td>
                      <td className="px-4 py-3 text-zinc-400">{p.client}</td>
                      <td className="px-4 py-3"><span className="font-mono text-[10px] text-volt uppercase">{p.sector}</span></td>
                      <td className="px-4 py-3 text-zinc-400">{p.location}</td>
                      <td className="px-4 py-3 font-mono text-zinc-400">{p.year}</td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 border border-white/10 hover:border-volt hover:text-volt mr-2"><Edit3 size={12} /></button>
                        <button data-testid={`delete-project-${p.id}`} onClick={() => delProject(p.id)} className="p-1.5 border border-white/10 hover:border-red-500 hover:text-red-500"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-zinc-500">No projects yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div>
            <h1 className="font-display uppercase text-3xl mb-6">Contact submissions</h1>
            <div className="grid gap-4">
              {contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={`admin-contact-${c.id}`}
                  className={`border p-5 ${c.read ? "border-white/10 bg-[#0F2952]" : "border-volt/40 bg-[#153264]"}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <div className="font-display uppercase text-lg">{c.name}</div>
                        {!c.read && <span className="font-mono text-[9px] uppercase text-black bg-volt px-2 py-0.5">New</span>}
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500 uppercase">
                        {c.email} {c.company && `· ${c.company}`} {c.phone && `· ${c.phone}`}
                      </div>
                      {c.interest && <div className="font-mono text-[10px] text-volt uppercase mt-1">Interest: {c.interest}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      {!c.read && (
                        <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="p-2 border border-white/15 hover:border-volt hover:text-volt"><MailOpen size={14} /></button>
                      )}
                      <button onClick={() => delContact(c.id)} data-testid={`delete-contact-${c.id}`} className="p-2 border border-white/15 hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                  <div className="mt-4 font-mono text-[10px] text-zinc-600 uppercase">{new Date(c.created_at).toLocaleString()}</div>
                </div>
              ))}
              {contacts.length === 0 && (
                <div className="text-center py-10 font-mono text-xs text-zinc-500">No submissions yet.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Project modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-[#081936]/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="admin-project-modal">
          <form onSubmit={saveProject} className="w-full max-w-2xl border border-white/10 bg-[#0F2952] p-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display uppercase text-2xl">{editing ? "Edit project" : "Add project"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 border border-white/15 hover:border-volt hover:text-volt"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <F label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="p-title" required />
              <F label="Client *" value={form.client} onChange={(v) => setForm({ ...form, client: v })} testid="p-client" required />
              <F label="Location *" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="p-location" required />
              <F label="Year *" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="p-year" required />
              <div className="col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Sector *</label>
                <select
                  value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm"
                  data-testid="p-sector"
                >
                  {SECTORS.map((s) => <option key={s} className="bg-[#0F2952]">{s}</option>)}
                </select>
              </div>
              <F label="Image URL" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="p-image" full />
              <div className="col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Scope *</label>
                <textarea
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  rows={4}
                  required
                  data-testid="p-scope"
                  className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost px-4 py-2 font-mono text-[11px] uppercase tracking-widest">Cancel</button>
              <button type="submit" data-testid="admin-save-project" className="btn-volt px-5 py-2 font-mono text-[11px] uppercase tracking-widest">{editing ? "Save changes" : "Create project"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, testid, required, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm"
      />
    </div>
  );
}
